/**
 * SmartAlertsWidget - Widget dashboard per Smart Alerts
 * Il Diario di Bordo - SaaS
 *
 * Mostra conteggio alert per severity e preview ultimi 3.
 * Visibile a coordinatori ed educatori (in base alla configurazione per-regola).
 * R04 (segnalazione urgente): dismiss permanente con label "Segna come gestita".
 * Altre regole: snooze intelligente (1g critical/warning, 7g info).
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFeature } from '../../hooks/useFeature'
import { alertsApi } from '../../services/api'
import { Bell, AlertTriangle, AlertCircle, Info, Check, X } from 'lucide-react'

const severityConfig = {
  critical: { icon: AlertTriangle, bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-300', badge: 'bg-red-500' },
  warning: { icon: AlertCircle, bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-300', badge: 'bg-amber-500' },
  info: { icon: Info, bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300', badge: 'bg-blue-500' }
}

export default function SmartAlertsWidget() {
  const { visible } = useFeature('smartAlerts')
  const navigate = useNavigate()
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!visible) return
    loadAlerts()
  }, [visible])

  const loadAlerts = async () => {
    try {
      setLoading(true)
      const res = await alertsApi.list()
      setAlerts(res.data?.data || [])
    } catch {
      // 403 = feature non abilitata, silenzioso
    } finally {
      setLoading(false)
    }
  }

  const handleDismiss = async (alertId) => {
    try {
      await alertsApi.dismiss(alertId)
      setAlerts(prev => prev.filter(a => a._id !== alertId))
    } catch {}
  }

  const handleMarkRead = async (alertId) => {
    try {
      await alertsApi.markRead(alertId)
      setAlerts(prev => prev.map(a => a._id === alertId ? { ...a, read: true } : a))
    } catch {}
  }

  if (!visible || (alerts.length === 0 && !loading)) return null

  // Conteggi per severity
  const counts = { critical: 0, warning: 0, info: 0 }
  alerts.forEach(a => { counts[a.severity] = (counts[a.severity] || 0) + 1 })

  const preview = alerts.slice(0, 3)

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Bell className="h-5 w-5 mr-2 text-indigo-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Smart Alerts</h3>
        </div>
        <div className="flex items-center gap-2">
          {counts.critical > 0 && (
            <span className="px-1.5 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full">
              {counts.critical}
            </span>
          )}
          {counts.warning > 0 && (
            <span className="px-1.5 py-0.5 text-xs font-bold text-white bg-amber-500 rounded-full">
              {counts.warning}
            </span>
          )}
          {counts.info > 0 && (
            <span className="px-1.5 py-0.5 text-xs font-bold text-white bg-blue-500 rounded-full">
              {counts.info}
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="h-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500" />
        </div>
      ) : (
        <div className="space-y-2">
          {preview.map(alert => {
            const config = severityConfig[alert.severity] || severityConfig.info
            const Icon = config.icon

            return (
              <div
                key={alert._id}
                className={`p-2.5 rounded-lg ${config.bg} ${!alert.read ? 'ring-1 ring-inset ring-current/10' : ''} ${alert.ruleId === 'R04' && alert.metadata?.entryId ? 'cursor-pointer' : ''}`}
                onClick={() => {
                  if (!alert.read) handleMarkRead(alert._id)
                  if (alert.ruleId === 'R04' && alert.metadata?.entryId) {
                    navigate(`/entries/${alert.metadata.entryId}/edit`)
                  }
                }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${config.text}`} />
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${config.text} truncate`}>
                        {alert.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5">
                        {alert.message}
                      </p>
                    </div>
                  </div>
                  {alert.ruleId === 'R04' ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDismiss(alert._id) }}
                      className={`px-2 py-0.5 text-xs rounded font-medium flex-shrink-0 ${config.text} bg-white/50 dark:bg-black/20 hover:bg-white/80 dark:hover:bg-black/40 border border-current/20`}
                      title="Segna la segnalazione come gestita (non riappare)"
                    >
                      Gestita
                    </button>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDismiss(alert._id) }}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
                      title="Snooze: l'alert scomparirà temporaneamente"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )
          })}

          {alerts.length > 3 && (
            <p className="text-xs text-gray-400 text-center">
              +{alerts.length - 3} altri alert
            </p>
          )}
        </div>
      )}
    </div>
  )
}
